import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Phone, MessageSquare, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ORB_LOGO = "https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/jrh9uqnh_Untitled%20design%20%287%29.png";
const FAVICON_GREEN_180 = "/favicon-180.png?v=2026-05-17";
const FAVICON_GREEN_48 = "/favicon-48.png?v=2026-05-17";
const FAVICON_GREEN_32 = "/favicon-32.png?v=2026-05-17";
const FAVICON_GREEN_16 = "/favicon-16.png?v=2026-05-17";
const FAVICON_GREEN_192 = "/icons/icon-192x192.png?v=2026-05-17";

/**
 * Email-signature EMAIL gate (iter175br).
 *
 * Twin of SigCallPage, but the form's copy is for email routing and
 * the submit handler hits /api/c/sig-email/{token}/route which
 * returns the resolved mailto: URI:
 *   • exemption_direct  → user's exemption ORB email (paste as-is)
 *   • statewide_plus_addressing → <state>+<routing_token>@<domain>
 *     so the inbound parser drops the message into the sig owner's
 *     in-app inbox via the user-routing-token mechanism in
 *     server.py::_ensure_user_routing_token.
 */
const SigEmailPage = () => {
  const { token } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const [centerOffset, setCenterOffset] = useState(0);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', notes: '', sms_consent: false });
  const [submitting, setSubmitting] = useState(false);

  // iter175bj — mount-only favicon + background + tab title setup.
  // Identical to SigCallPage; the favicon is locked to the green
  // branded ORB asset (no user-photo swap) because iOS Safari
  // rejected arbitrary remote profile-picture URLs as
  // apple-touch-icon and dropped to a blank tab strip.
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body.style.background;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta ? themeMeta.getAttribute('content') : null;
    document.documentElement.style.background = '#000';
    document.body.style.background = '#000';
    if (themeMeta) themeMeta.setAttribute('content', '#000000');

    const head = document.head;
    const originalIcons = [];
    head.querySelectorAll("link[rel*='icon']").forEach((el) => {
      originalIcons.push(el);
      el.parentNode.removeChild(el);
    });
    const inject = (rel, sizes, href) => {
      const l = document.createElement('link');
      l.rel = rel;
      if (sizes) l.setAttribute('sizes', sizes);
      l.type = 'image/png';
      l.href = `${href}${href.includes('?') ? '&' : '?'}sigemail=${Date.now()}`;
      l.dataset.sigEmailTemp = '1';
      head.appendChild(l);
    };
    inject('apple-touch-icon', null, FAVICON_GREEN_180);
    inject('apple-touch-icon', '180x180', FAVICON_GREEN_180);
    inject('icon', '192x192', FAVICON_GREEN_192);
    inject('icon', '48x48', FAVICON_GREEN_48);
    inject('icon', '32x32', FAVICON_GREEN_32);
    inject('icon', '16x16', FAVICON_GREEN_16);
    inject('shortcut icon', null, FAVICON_GREEN_180);

    return () => {
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.background = prevBodyBg;
      if (themeMeta && prevTheme !== null) themeMeta.setAttribute('content', prevTheme);
      Array.from(head.querySelectorAll("link[data-sig-email-temp='1']")).forEach((el) => el.remove());
      originalIcons.forEach((el) => head.appendChild(el));
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/card/${encodeURIComponent(token)}`);
        if (!r.ok) {
          setError('This contact is no longer available.');
          setLoading(false);
          return;
        }
        const d = await r.json();
        if (!cancelled) {
          setCardData(d);
          setLoading(false);
        }
      } catch (_e) {
        if (!cancelled) {
          setError('Network error. Please try again later.');
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!cardData) return;
    const first = (cardData?.first_name || (cardData?.full_name || cardData?.name || '').split(' ')[0] || '').trim();
    if (first) {
      document.title = `Email ${first}`;
    }
  }, [cardData]);

  // iPad portrait centering (mirrors SigCallPage). On phones (mobile
  // portrait) the centerOffset stays at 0 so the card hugs the top
  // of the viewport — that's why the call-page and email-page
  // backgrounds line up. Without this guard the card was pushed
  // down by ~half the available viewport on phones.
  useEffect(() => {
    if (!wrapRef.current) return;
    const isTabletPortrait = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (orientation: portrait)').matches;
    const measure = () => {
      if (!isTabletPortrait()) { setCenterOffset(0); return; }
      const cardEl = wrapRef.current.querySelector('[data-testid="sig-email-card-container"]');
      if (!cardEl) return;
      const vh = window.innerHeight;
      const cardH = cardEl.getBoundingClientRect().height;
      setCenterOffset(Math.max(0, Math.floor((vh - cardH) / 2 - 20)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cardData]);

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center" data-testid="sig-email-loading">
        <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-screen bg-black flex items-center justify-center p-6" data-testid="sig-email-error">
        <div className="text-center">
          <img src={ORB_LOGO} alt="ORB" className="h-12 mx-auto mb-6 object-contain" />
          <p className="text-gray-500 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const firstName = (
    cardData?.first_name
    || (cardData?.full_name || cardData?.name || '').split(' ')[0]
    || 'them'
  ).trim();
  const name = cardData?.full_name || cardData?.name || '';
  const title = cardData?.title || '';
  const company = cardData?.company || 'Collective Solutions Inc.';
  const profilePicture = cardData?.profile_picture || cardData?.photo_url || '';
  const initials = (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/c/sig-email/${encodeURIComponent(token)}/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          notes: form.notes,
          sms_consent: !!form.sms_consent,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.mailto) {
        toast.error(j?.detail || 'Could not route your email. Please try again.');
        setSubmitting(false);
        return;
      }
      setTimeout(() => { window.location.href = j.mailto; }, 0);
    } catch (_e) {
      toast.error('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 bg-black overflow-y-auto flex flex-col items-center"
      style={{
        paddingTop: `calc(env(safe-area-inset-top, 0px) + 20px + ${centerOffset}px)`,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
      }}
      data-testid="sig-email-page"
    >
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />

      <div
        className="w-[92vw] max-w-[420px] bg-white border-2 border-black rounded-3xl flex flex-col items-center relative overflow-hidden shadow-2xl"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif" }}
        data-testid="sig-email-card-container"
      >
        <div className="w-full flex flex-col items-center px-6 pt-[2vh] pb-4">
          <img src={ORB_LOGO} alt="ORB" className="h-[11vh] object-contain mb-[1vh]" />
          <div className="mb-[0.8vh]">
            <Avatar className="h-[22vh] w-[16vh] rounded-2xl border-2 border-gray-200">
              <AvatarImage src={profilePicture} className="object-cover object-top" style={{ aspectRatio: 'auto' }} />
              <AvatarFallback className="bg-gradient-to-br from-[#586D52] to-[#3a4d36] text-white text-6xl rounded-2xl">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>
          <h1 className="text-2xl font-extrabold text-black leading-tight tracking-tight">{name}</h1>
          {title ? <p className="text-gray-600 text-base capitalize font-medium">{title}</p> : null}
          <p className="text-gray-500 text-[11px] tracking-[0.2em] uppercase mb-[1.2vh] font-semibold">{company}</p>
          <div className="w-full h-px bg-gray-200 mt-[0.55vh] mb-[1.2vh]" />
          <div className="flex items-center justify-center gap-4 mb-[1.2vh]">
            {[
              { icon: <Mail className="w-5 h-5" />, label: 'Email' },
              { icon: <Phone className="w-5 h-5" />, label: 'Call' },
              { icon: <MessageSquare className="w-5 h-5" />, label: 'Text' },
              { icon: <UserPlus className="w-5 h-5" />, label: 'Contact' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-0.5">
                <div className="h-11 w-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-700">
                  {item.icon}
                </div>
                <span className="text-gray-600 text-[11px] font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm" />

      <div
        className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-gradient-to-b from-[#f6faf3] via-[#7f8d79] via-30% via-[#222] via-55% to-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
        style={{
          top: 'calc(42vh + 40px)',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif",
        }}
        data-testid="sig-email-popup"
      >
        <div className="flex-1 overflow-hidden px-6 pt-5 pb-6 space-y-3" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))', overscrollBehavior: 'contain' }}>
          <h3 className="text-lg font-bold text-center text-gray-900" data-testid="sig-email-heading">
            Please provide your information so we can route your email to {firstName}
          </h3>

          <div className="flex gap-2">
            <Input placeholder="First Name *" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-email-first-name" />
            <Input placeholder="Last Name *" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-email-last-name" />
          </div>

          <div className="flex gap-2">
            <Input type="email" placeholder="Email *" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-email-email" />
            <Input type="tel" placeholder="Phone *" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-email-phone" />
          </div>

          <textarea
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none h-16 bg-white text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#586D52]/30"
            data-testid="sig-email-notes"
          />

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.sms_consent}
              onChange={e => setForm(p => ({ ...p, sms_consent: e.target.checked }))}
              className="mt-0.5 accent-[#586D52] h-4 w-4 shrink-0"
              data-testid="sig-email-sms-consent"
            />
            <span className="text-[11px] text-white leading-snug">
              By checking this box, you agree to receive sms messages from us. Message frequency varies & data rates may apply. Reply stop to opt out.{' '}
              <a href="https://www.collectiveorb.com/privacy-policy" target="_blank" rel="noreferrer" className="underline text-white font-medium">Privacy Policy</a>.
            </span>
          </label>

          <Button
            className="w-full bg-[#586D52] hover:bg-[#4a5d46] text-white text-base font-bold py-3 rounded-xl shadow-md"
            onClick={submit}
            disabled={submitting}
            data-testid="sig-email-submit-btn"
          >
            {submitting ? 'Connecting…' : `Email ${firstName}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SigEmailPage;
